<script setup lang="ts">
const props = withDefaults(defineProps<{
  modelValue?: number
  length?: number
  totalVisible?: number
  density?: 'default' | 'comfortable' | 'compact'
  rounded?: boolean
  size?: 'small' | 'default' | 'large'
}>(), {
  modelValue: 1,
  length: 1,
  totalVisible: 7,
  density: 'default',
  size: 'default',
})

const emit = defineEmits<{
  'update:modelValue': [val: number]
}>()

const pages = computed(() => {
  const total = props.length
  const current = props.modelValue
  const visible = props.totalVisible

  if (total <= visible) return Array.from({ length: total }, (_, i) => i + 1)

  const pages: (number | string)[] = []
  const half = Math.floor(visible / 2)

  if (current <= half + 1) {
    for (let i = 1; i <= visible - 2; i++) pages.push(i)
    pages.push('...', total)
  } else if (current >= total - half) {
    pages.push(1, '...')
    for (let i = total - visible + 3; i <= total; i++) pages.push(i)
  } else {
    pages.push(1, '...')
    for (let i = current - half + 2; i <= current + half - 2; i++) pages.push(i)
    pages.push('...', total)
  }

  return pages
})
</script>

<template>
  <nav class="m-pagination" :class="`m-pagination--${size}`" aria-label="Pagination">
    <MBtn
      icon
      variant="text"
      :size="size"
      :disabled="modelValue <= 1"
      @click="emit('update:modelValue', modelValue - 1)"
    >
      <MIcon icon="chevron-left" :size="size" />
    </MBtn>

    <template v-for="(page, i) in pages" :key="i">
      <span v-if="page === '...'" class="m-pagination__ellipsis">...</span>
      <MBtn
        v-else
        icon
        :variant="page === modelValue ? 'flat' : 'text'"
        :color="page === modelValue ? 'primary' : undefined"
        :size="size"
        @click="emit('update:modelValue', page as number)"
      >
        {{ page }}
      </MBtn>
    </template>

    <MBtn
      icon
      variant="text"
      :size="size"
      :disabled="modelValue >= length"
      @click="emit('update:modelValue', modelValue + 1)"
    >
      <MIcon icon="chevron-right" :size="size" />
    </MBtn>
  </nav>
</template>

<style scoped>
.m-pagination {
  display: flex;
  align-items: center;
  gap: var(--m-space-1);
}

.m-pagination__ellipsis {
  padding: 0 var(--m-space-2);
  color: var(--m-on-surface-variant);
}
</style>
